import { useRef, useState } from 'react';
import { Alert, Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { ContactForm } from '../../PublicBookingPage.hooks';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

type Props = {
  contact: ContactForm;
  onChange: (field: keyof ContactForm, value: string) => void;
  photos: string[];
  maxPhotos: number;
  onAddPhoto: (dataUrl: string) => void;
  onRemovePhoto: (index: number) => void;
};

const PRIMARY = '#D02964';
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px' } };

const compressImage = (imageData: string, maxWidth = 1200, quality = 0.7): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = imageData;
  });

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const StepContact = ({
  contact,
  onChange,
  photos,
  maxPhotos,
  onAddPhoto,
  onRemovePhoto,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const { t } = useIntlTranslations('stepContact');
  const emailInvalid =
    contact.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim());

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoError(t('errorNotImage'));
      return;
    }
    if (photos.length >= maxPhotos) return;

    setPhotoError(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const compressed = await compressImage(dataUrl);
      onAddPhoto(compressed);
    } catch {
      setPhotoError(t('errorLoadFailed'));
    }
  };

  const canAddMore = photos.length < maxPhotos;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700} color="text.primary">
        {t('title')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          label={t('firstName')}
          name="firstName"
          autoComplete="given-name"
          value={contact.firstName}
          onChange={e => onChange('firstName', e.target.value)}
          fullWidth
          required
          size="small"
          sx={inputSx}
        />
        <TextField
          label={t('lastName')}
          name="lastName"
          autoComplete="family-name"
          value={contact.lastName}
          onChange={e => onChange('lastName', e.target.value)}
          fullWidth
          required
          size="small"
          sx={inputSx}
        />
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          label={t('phonePrefix')}
          name="phonePrefix"
          autoComplete="tel-country-code"
          type="tel"
          value={contact.phonePrefix}
          onChange={e => onChange('phonePrefix', e.target.value)}
          sx={{ width: 110, flexShrink: 0, ...inputSx }}
          size="small"
        />
        <TextField
          label={t('phone')}
          name="phone"
          autoComplete="tel-national"
          type="tel"
          value={contact.phone}
          onChange={e => onChange('phone', e.target.value)}
          fullWidth
          required
          size="small"
          sx={inputSx}
        />
      </Stack>
      <TextField
        label={t('email')}
        name="email"
        autoComplete="email"
        error={emailInvalid}
        helperText={emailInvalid ? t('emailInvalid') : undefined}
        value={contact.email}
        onChange={e => onChange('email', e.target.value)}
        fullWidth
        type="email"
        size="small"
        required
        sx={inputSx}
      />
      <TextField
        label={t('note')}
        value={contact.note}
        onChange={e => onChange('note', e.target.value)}
        fullWidth
        multiline
        rows={3}
        size="small"
        sx={inputSx}
      />

      {/* Fotky zákazníka (volitelné, max. 2) */}
      <Box>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
          {t('photosTitle')}
        </Typography>
        <Alert variant="filled" severity="warning">
          {t('photosHint', { max: maxPhotos })}
        </Alert>

        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap" rowGap={1.5}>
          {photos.map((photo, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 88,
                height: 88,
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <img
                src={photo}
                alt={t('photoAlt', { index: index + 1 })}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <IconButton
                size="small"
                onClick={() => onRemovePhoto(index)}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 22,
                  height: 22,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {canAddMore && (
            <Box
              onClick={() => inputRef.current?.click()}
              sx={{
                width: 88,
                height: 88,
                borderRadius: '12px',
                border: '2px dashed',
                borderColor: `${PRIMARY}80`,
                color: PRIMARY,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: `${PRIMARY}08`,
                '&:hover': { bgcolor: `${PRIMARY}15` },
              }}
            >
              <AddPhotoAlternateIcon sx={{ fontSize: 26 }} />
              <Typography variant="caption" fontWeight={600}>
                {t('addPhoto')}
              </Typography>
            </Box>
          )}
        </Stack>

        {photoError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {photoError}
          </Typography>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
    </Stack>
  );
};

export default StepContact;
